/* eslint-disable @typescript-eslint/no-explicit-any  */
/* eslint-disable no-useless-escape */

import { AxiosError } from 'axios';
import i18next from 'i18next';
import Cookies from 'js-cookie';

import { Http, HttpInstance } from '@mygpt/app/http';

import { KEY_MYGPT_TOKEN } from '../constant';
import { errorModal, loginExpired, translateError } from './requestError';

/** API 文档  **/
// https://api.gbase.ai/docs-swagger#/
// 本地开发优先走 /api 代理，线上走 api 地址

interface InitInstanceParams {
  baseUrl: string;
  baseHost: string;
  isAdmin?: boolean;
}

let instance: HttpInstance;
export let baseUrl: string;
export let baseHost: string;
export let isAdmin: boolean;

export const initInstance = ({
  baseHost: envBaseHost,
  baseUrl: envBaseUrl,
  isAdmin: envIsAdmin,
}: InitInstanceParams) => {
  baseUrl = envBaseUrl;
  baseHost = envBaseHost;
  isAdmin = Boolean(envIsAdmin);
  instance = Http.init({
    baseURL: envBaseUrl,
  });

  /** request */
  instance.interceptors.request.use((req) => {
    if (!req.headers['notNeedToken'] || req.headers['notNeedToken'] === 'false') {
      const token = headerToken();
      req.headers.Authorization = token;
    }
    Object.prototype.hasOwnProperty.call(req.headers, 'notNeedToken') && delete req.headers['notNeedToken'];
    return req;
  });

  /** response */
  instance.interceptors.response.use(
    (response) => {
      return response;
    },
    async (error: AxiosError<any, any>) => {
      if (!error.response) {
        return Promise.reject(i18next.t('completions.server_error'));
      }

      let errorMsg = '';

      // 检查是否设置了跳过错误提示的配置
      const skipErrorNotification = error.config?.skipErrorNotification;

      // 定义正则表达式以匹配不固定前缀后的 "/questions/" 后面跟随任意字符，但不超过这一层路径
      const localQuestionsRegex = /^(https?:\/\/[^\/]+\/[^\/]+\/)questions\/[^\/]+?$/;
      const questionsRegex = /^(https?:\/\/[^\/]+)\/questions\/[^\/]+$/;
      const excludedRegex = [localQuestionsRegex, questionsRegex];
      const url = (error.response as any)?.url || error.response?.config.url || '';
      const noRedirectURL = ['reclassify', 'tag-groups', 'tags'];
      const isTagApi = noRedirectURL.some((path) => url.includes(path));

      // 排除不需要弹窗错误的接口
      let isExcludedApi = false;
      if (excludedRegex.some((regx) => regx.test(url))) isExcludedApi = true;
      if (skipErrorNotification) isExcludedApi = true;

      if (!isExcludedApi && error && error.response) {
        const status = error.response.status;
        const errMsg = error.response && error.response.data ? error.response.data.detail : error.response.statusText;
        /**
         * 错误信息
         * https://l8jshxfw5fs.jp.larksuite.com/wiki/A6YzwCBiHif0ZVkIeZLjV8REp49
         */
        switch (Number(status)) {
          case 401:
            loginExpired();
            break;
          case 403:
            if (isTagApi) {
              errorModal(i18next.t('tag.no_permission'));
              break;
            }

            if (!headerToken()) {
              loginExpired();
            } else {
              errorModal(i18next.t('no_auth'));
              window.location.href = '/no-access';
            }
            break;
          // 逐步优化错误处理逻辑，大部分错误不需要统一处理，因为需要根据场景设置不同的提示语。
          // case 404:
          //   errorModal(i18next.t('errors.not_found'));
          //   break;
          case 422:
            errorModal(i18next.t('errors.has_change'));
            break;
          case 500:
            errorModal(i18next.t('completions.server_error'));
            break;
          default: {
            errorMsg = translateError(errMsg);
            errorModal(errorMsg);
            break;
          }
        }
        // 不上报sentry错误
        if ([401, 403].includes(status) && !isTagApi) {
          return new Promise(() => {});
        }
      }

      // 如果是stream的错误另外处理
      const body = (error.response as any).body;
      if (isReadableStream(body)) {
        const reader = body.getReader();
        const decoder = new TextDecoder();
        const { value } = await reader.read();
        const text = decoder.decode(value, { stream: true });
        let errorText = i18next.t('completions.server_error');
        if (text) {
          try {
            const rspError = typeof text === 'string' ? JSON.parse(text) : { detail: text };
            if (rspError.detail) {
              errorText = rspError.detail;
            }
          } catch (error) {
            console.error(error);
          }
          return Promise.reject(translateError(errorText));
        }
      }

      return Promise.reject(errorMsg);
    }
  );
};

const headerToken = () => `${Cookies.get(KEY_MYGPT_TOKEN()) || ''}`;

const isReadableStream = (body: ReadableStream<Uint8Array> | undefined): body is ReadableStream => {
  return body instanceof ReadableStream;
};
